
import React from 'react'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import CreateTaskDialog from './CreateTaskDialog'
import { useTasks } from '@/hooks/useTasks'
import { useModulesForDropdown } from '@/hooks/useModulesForDropdown'

const domains = [
  { value: 'self', label: 'Self' },
  { value: 'wealth', label: 'Wealth' },
  { value: 'social', label: 'Social' }
]

const viewOptions = [
  { value: 'my-tasks', label: 'My Tasks' },
  { value: 'discover-tasks', label: 'Discover Tasks' },
  { value: 'archived-tasks', label: 'Archived Tasks' }
]

interface TaskFiltersProps {
  selectedDomain: string
  selectedModule: string
  selectedView: string
  onDomainChange: (value: string) => void
  onModuleChange: (value: string) => void
  onViewChange: (value: string) => void
}

const TaskFilters: React.FC<TaskFiltersProps> = ({
  selectedDomain,
  selectedModule,
  selectedView,
  onDomainChange,
  onModuleChange,
  onViewChange
}) => {
  const { createCustomTask } = useTasks()
  const { modules: allModules, loading: modulesLoading } = useModulesForDropdown()
  
  // Filter modules by selected domain using actual database categories
  const availableModules = allModules.filter(module => {
    if (!selectedDomain) return true // Show all if no domain selected
    
    // Map domain selection to actual module categories from database
    if (selectedDomain === 'wealth') {
      return module.category === 'Wealth'
    } else if (selectedDomain === 'self') {
      return module.category === 'Self'
    } else if (selectedDomain === 'social') {
      return module.category === 'Social'
    }
    return false
  })

  // Clear module selection when domain changes
  const handleDomainChange = (domain: string) => {
    onDomainChange(domain)
    onModuleChange('') // Clear module selection
  }

  return (
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-4">
        <span className="text-sm font-medium text-gray-700">Filter:</span>
        
        {/* Domain Filter */}
        <Select value={selectedDomain} onValueChange={handleDomainChange}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Select Domain" />
          </SelectTrigger>
          <SelectContent>
            {domains.map(domain => (
              <SelectItem key={domain.value} value={domain.value}>
                {domain.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Module Filter */}
        <Select value={selectedModule} onValueChange={onModuleChange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={modulesLoading ? "Loading..." : "Select Module"} />
          </SelectTrigger>
          <SelectContent>
            {availableModules.map(module => (
              <SelectItem key={module.value} value={module.value}>
                {module.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select value={selectedView} onValueChange={onViewChange}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Select Status" />
          </SelectTrigger>
          <SelectContent>
            {viewOptions.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-3">
        <CreateTaskDialog onCreateTask={createCustomTask} />
        <Button variant="outline" size="icon">
          <Search className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}

export default TaskFilters
